import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScienceIcon from '@mui/icons-material/Science';
import LayersIcon from '@mui/icons-material/Layers';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApolloClient } from '@apollo/client';
import {
  useTestGroupsGet,
  useTestGroupPricesGet,
  useTestGroupCreate,
  useTestGroupIdsGet,
  useTestGroupValidate,
  useTestGroupDelete,
  TestGroup,
  TestGroupPrice,
  PriceTier,
  TestGroupValidationResult,
  TEST_GROUP_PARENT_GROUP_ID,
  TEST_GROUP_TYPE,
  TEST_GROUPS_GET,
  TEST_GROUP_VALIDATE,
} from '../../../clients/graphql/pages/test-groups-queries';

const formatEnumValue = (value: string): string => {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return `$${(amount / 100).toFixed(2)}`;
};

const formatYearlyPercent = (value: string | null | undefined): string => {
  if (!value) return '—';
  return `${parseFloat(parseFloat(value).toFixed(3))}%`;
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
  </Box>
);

// Validation indicator component for each group
const ValidationIndicator = ({
  parentGroupId,
  groupId,
}: {
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  groupId: string;
}) => {
  const [showReasons, setShowReasons] = useState(false);
  const { data, loading, error } = useTestGroupValidate({ parentGroupId, groupId });
  const validation = data?.testGroupValidate;

  if (loading) {
    return <CircularProgress size={16} />;
  }

  if (error) {
    return (
      <Chip
        icon={<InfoIcon sx={{ fontSize: 16 }} />}
        label="Error"
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontSize: '0.7rem' }}
        title={error.message}
      />
    );
  }

  if (!validation) {
    return null;
  }

  if (validation.isValid) {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
        label="Valid"
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: '0.7rem' }}
      />
    );
  }

  return (
    <>
      <Chip
        icon={<ErrorIcon sx={{ fontSize: 16 }} />}
        label="Invalid"
        size="small"
        color="error"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          setShowReasons(true);
        }}
        sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
      />
      <Dialog
        open={showReasons}
        onClose={() => setShowReasons(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #fca5a5'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                backgroundColor: '#dc2626',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ErrorIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#991b1b">
                  Validation Failed
                </Typography>
                <Typography variant="body2" color="#b91c1c">
                  {groupId}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setShowReasons(false)}
              size="small"
              sx={{
                color: '#991b1b',
                '&:hover': { backgroundColor: 'rgba(153, 27, 27, 0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {validation.failedReasons.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {validation.failedReasons.length} issue{validation.failedReasons.length !== 1 ? 's' : ''} found
              </Typography>
              {validation.failedReasons.map((reason: string, idx: number) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: '#fee2e2',
                    backgroundColor: '#fef2f2',
                    borderLeft: '3px solid #ef4444'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: '#fecaca',
                        color: '#991b1b',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 600,
                        minWidth: 24,
                        textAlign: 'center'
                      }}
                    >
                      {idx + 1}
                    </Typography>
                    <Typography variant="body2" color="#7f1d1d" sx={{ flex: 1 }}>
                      {reason}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 3,
              color: 'text.secondary'
            }}>
              <InfoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                No specific reasons provided.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Available Group IDs section
const AvailableGroupIdsSection = ({
  parentGroupId,
  existingGroupIds,
}: {
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  existingGroupIds: string[];
}) => {
  const { data, loading, error } = useTestGroupIdsGet({ parentGroupId });
  const availableIds = data?.testGroupIdsGet?.groupIds || [];

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #bae6fd',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading available groups...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading available group IDs: {error.message}
        </Alert>
      </Box>
    );
  }

  if (availableIds.length === 0) {
    return null;
  }

  const usedCount = existingGroupIds.length;
  const totalCount = availableIds.length;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <LayersIcon sx={{ color: '#64748b', fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={600} color="#475569">
          Available Groups
        </Typography>
        <Typography variant="caption" color="#94a3b8">
          ({usedCount}/{totalCount} in use)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {availableIds.map((id: string) => {
          const isUsed = existingGroupIds.includes(id);
          return (
            <Chip
              key={id}
              icon={isUsed ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
              label={id}
              size="small"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: isUsed ? '#6366f1' : '#f8fafc',
                color: isUsed ? 'white' : '#64748b',
                border: isUsed ? 'none' : '1px solid #e2e8f0',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          );
        })}
      </Box>
    </Paper>
  );
};

const PriceDetailModal = ({ price, open, onClose }: { price: TestGroupPrice | null; open: boolean; onClose: () => void }) => {
  if (!price) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#64748b' }}>
              {price.id}
            </Typography>
            {price.nickname && (
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.85rem' }}>
                - {price.nickname}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <DetailRow label="Billing Scheme" value={price.billingScheme ? formatEnumValue(price.billingScheme) : null} />
          <DetailRow label="Interval" value={price.displayInterval} />
          <DetailRow label="Annual AUM Fee" value={price.annualAUMFeePercent != null ? `${price.annualAUMFeePercent}%` : null} />
          <DetailRow label="Unit Amount" value={price.unitAmount != null ? formatCurrency(price.unitAmount) : null} />
          <DetailRow label="Usage Type" value={price.usageType ? formatEnumValue(price.usageType) : null} />
          <DetailRow label="Tiers Mode" value={price.tiersMode ? formatEnumValue(price.tiersMode) : null} />
        </Box>

        {price.tiers && price.tiers.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pricing Tiers
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Up To</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Yearly</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Flat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {price.tiers.map((tier: PriceTier, idx: number) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>{tier.upTo != null ? `$${tier.upTo.toLocaleString()}` : '∞'}</TableCell>
                      <TableCell align="right">{formatYearlyPercent(tier.yearlyAmountDecimal)}</TableCell>
                      <TableCell align="right">{formatCurrency(tier.flatAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AddToTestGroupModal = ({
  price,
  parentGroupId,
  open,
  onClose,
  onSuccess,
}: {
  price: TestGroupPrice | null;
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState('');
  const [testGroupCreate, { loading }] = useTestGroupCreate();
  const { data: groupIdsData, loading: groupIdsLoading } = useTestGroupIdsGet({ parentGroupId });
  const availableGroupIds = groupIdsData?.testGroupIdsGet?.groupIds || [];

  const handleSubmit = async () => {
    if (!groupId) {
      setError('Group ID is required');
      return;
    }

    if (!price) return;

    try {
      await testGroupCreate({
        variables: {
          input: {
            parentGroupId,
            groupId,
            type: TEST_GROUP_TYPE.STRIPE_PRICE_ENTITY,
            typeKey: price.id,
          },
        },
        refetchQueries: [
          { query: TEST_GROUPS_GET, variables: { input: { parentGroupId } } },
          { query: TEST_GROUP_VALIDATE, variables: { input: { parentGroupId, groupId } } },
        ],
      });
      setGroupId('');
      setError('');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add to test group');
    }
  };

  const handleClose = () => {
    setGroupId('');
    setError('');
    onClose();
  };

  if (!price) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add to Test Group</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Price
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748b' }}>
              {price.id}
            </Typography>
            {price.nickname && (
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.75rem', ml: 1 }}>
                - {price.nickname}
              </Typography>
            )}
          </Box>
        </Box>
        <FormControl fullWidth size="small" sx={{ mt: 1 }} error={!!error}>
          <InputLabel>Group ID</InputLabel>
          <Select
            value={groupId}
            label="Group ID"
            onChange={(e) => {
              setGroupId(e.target.value);
              setError('');
            }}
            disabled={groupIdsLoading}
          >
            {groupIdsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
              </MenuItem>
            ) : availableGroupIds.length === 0 ? (
              <MenuItem disabled>No groups available</MenuItem>
            ) : (
              availableGroupIds.map((id: string) => (
                <MenuItem key={id} value={id}>
                  {id}
                </MenuItem>
              ))
            )}
          </Select>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {error}
            </Typography>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !groupId}>
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PricesSection = ({
  prices,
  loading,
  error,
  parentGroupId,
  onTestGroupAdded,
}: {
  prices: TestGroupPrice[];
  loading: boolean;
  error: any;
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  onTestGroupAdded: () => void;
}) => {
  const [selectedPrice, setSelectedPrice] = useState<TestGroupPrice | null>(null);
  const [addPrice, setAddPrice] = useState<TestGroupPrice | null>(null);

  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
          Available Prices
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            backgroundColor: 'white',
          }}
        >
          <CircularProgress size={24} sx={{ color: '#94a3b8' }} />
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
          Available Prices
        </Typography>
        <Alert severity="error" sx={{ borderRadius: 2 }}>Error loading prices.</Alert>
      </Box>
    );
  }

  if (prices.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
          Available Prices
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            backgroundColor: 'white',
          }}
        >
          <Typography variant="body2" color="#64748b">
            No prices available. Configure prices in Stripe first.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const priceColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Price',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
          onClick={() => setSelectedPrice(params.row as TestGroupPrice)}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
            {params.value}
          </Typography>
          {params.row.nickname && (
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.75rem', ml: 1 }}>
              - {params.row.nickname}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'billingScheme',
      headerName: 'Type',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={params.value === 'TIERED' ? 'Tiered' : 'Flat'}
            size="small"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
              backgroundColor: params.value === 'TIERED' ? '#dbeafe' : '#dcfce7',
              color: params.value === 'TIERED' ? '#1e40af' : '#166534',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'displayInterval',
      headerName: 'Interval',
      width: 90,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'usageType',
      headerName: 'Usage',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const isMetered = params.value?.toUpperCase() === 'METERED';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              label={isMetered ? 'Metered' : 'Licensed'}
              size="small"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                backgroundColor: isMetered ? '#fef3c7' : '#e0e7ff',
                color: isMetered ? '#92400e' : '#3730a3',
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'unitAmount',
      headerName: 'Amount',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={500}>
            {params.row.billingScheme !== 'TIERED' && params.value != null
              ? formatCurrency(params.value)
              : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tiers',
      headerName: 'Tiers',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={params.value?.length || 0}
            size="small"
            sx={{
              fontSize: '0.7rem',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              minWidth: 28,
            }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', px: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              setAddPrice(params.row as TestGroupPrice);
            }}
            sx={{
              fontSize: '0.75rem',
              textTransform: 'none',
              borderRadius: 1.5,
              borderColor: '#e2e8f0',
              color: '#475569',
              '&:hover': {
                borderColor: '#6366f1',
                backgroundColor: '#f8fafc',
                color: '#6366f1',
              },
            }}
          >
            Add to Group
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
        Available Prices
      </Typography>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          backgroundColor: 'white',
        }}
      >
        <DataGrid
          rows={prices.map(p => ({ ...p }))}
          columns={priceColumns}
          rowHeight={52}
          sx={{
            border: 0,
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f8fafc' },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: '#475569',
              fontSize: '0.8rem',
            },
            '& .MuiDataGrid-cell': { borderColor: '#f1f5f9' },
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter={prices.length <= 10}
          autoHeight
        />
      </Paper>
      <PriceDetailModal
        price={selectedPrice}
        open={!!selectedPrice}
        onClose={() => setSelectedPrice(null)}
      />
      <AddToTestGroupModal
        price={addPrice}
        parentGroupId={parentGroupId}
        open={!!addPrice}
        onClose={() => setAddPrice(null)}
        onSuccess={onTestGroupAdded}
      />
    </Box>
  );
};

export const TestGroupDetailPage = () => {
  const { parentGroupId: parentGroupIdParam } = useParams<{ parentGroupId: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const parentGroupId = parentGroupIdParam as TEST_GROUP_PARENT_GROUP_ID;
  const isAumRollout = parentGroupId === TEST_GROUP_PARENT_GROUP_ID.AUM_ROLLOUT;
  const [selectedPrice, setSelectedPrice] = useState<TestGroupPrice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TestGroup | null>(null);
  const [testGroupDelete, { loading: deleteLoading }] = useTestGroupDelete();

  const { loading, error, data, refetch } = useTestGroupsGet(
    { parentGroupId },
    { skip: !parentGroupId }
  );

  const { loading: pricesLoading, error: pricesError, data: pricesData } = useTestGroupPricesGet({
    skip: !isAumRollout,
  });

  const testGroups: TestGroup[] = data?.testGroupsGet?.testGroups || [];
  const prices: TestGroupPrice[] = pricesData?.testGroupPricesGet?.prices || [];

  // Group test groups by groupId
  const groupedTestGroups = useMemo(() => {
    const groups: Record<string, TestGroup[]> = {};
    testGroups.forEach((tg) => {
      if (!groups[tg.groupId]) {
        groups[tg.groupId] = [];
      }
      groups[tg.groupId].push(tg);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [testGroups]);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await testGroupDelete({
        variables: {
          input: { testGroupKey: deleteTarget.testGroupKey },
        },
      });

      // Refetch test groups
      await refetch();

      // Refetch validation for this group
      apolloClient.refetchQueries({
        include: [TEST_GROUP_VALIDATE],
      });

      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete test group:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        gap: 2,
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
          borderRadius: '50%',
          p: 2,
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.1)', opacity: 0.8 },
          },
        }}>
          <ScienceIcon sx={{ color: 'white', fontSize: 40 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" fontWeight={500}>
          Loading Test Groups...
        </Typography>
        <CircularProgress size={24} sx={{ color: '#8b5cf6' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 3 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            '& .MuiAlert-icon': { alignItems: 'center' },
          }}
        >
          Error fetching test groups. Please reload the page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        px: 3,
        py: 2.5,
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/developer/test-groups')}
              sx={{
                color: '#64748b',
                '&:hover': { backgroundColor: '#f1f5f9' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{
              backgroundColor: '#f1f5f9',
              borderRadius: 1.5,
              p: 1,
              display: 'flex',
            }}>
              <ScienceIcon sx={{ color: '#6366f1', fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} color="#1e293b">
                {formatEnumValue(parentGroupId)}
              </Typography>
              <Typography variant="body2" color="#64748b">
                Test Group Configuration
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {groupedTestGroups.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Groups
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, borderLeft: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {testGroups.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Items
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, borderLeft: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {prices.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Prices
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
        <AvailableGroupIdsSection
          parentGroupId={parentGroupId}
          existingGroupIds={groupedTestGroups.map(([gId]) => gId)}
        />

        <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
          Test Groups
        </Typography>

        {testGroups.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              textAlign: 'center',
              backgroundColor: 'white',
            }}
          >
            <Typography variant="body2" color="#64748b">
              No test groups yet. Add prices using the section below.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {groupedTestGroups.map(([groupId, items]) => (
              <Accordion
                key={groupId}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px !important',
                  backgroundColor: 'white',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
                  sx={{
                    backgroundColor: '#f8fafc',
                    minHeight: 52,
                    '& .MuiAccordionSummary-content': { margin: '10px 0' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FolderIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                      {groupId}
                    </Typography>
                    <Chip
                      label={`${items.length}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        backgroundColor: '#e2e8f0',
                        color: '#475569',
                        fontWeight: 600,
                        minWidth: 24,
                        height: 22,
                      }}
                    />
                    <ValidationIndicator parentGroupId={parentGroupId} groupId={groupId} />
                  </Box>
                </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 90 }}>Billing</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 90 }}>Interval</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 90 }}>Usage</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 70 }}>Tiers</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 80 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#475569', width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((tg) => (
                        <TableRow
                          key={tg.testGroupKey}
                          onClick={() => tg.price && setSelectedPrice(tg.price)}
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            '&:hover': { backgroundColor: '#faf5ff' },
                            transition: 'background-color 0.15s',
                            cursor: tg.price ? 'pointer' : 'default',
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                                {tg.price?.id || tg.typeKey || '—'}
                              </Typography>
                              {tg.price?.nickname && (
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.75rem', ml: 1 }}>
                                  - {tg.price.nickname}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {tg.price?.billingScheme ? (
                              <Chip
                                label={tg.price.billingScheme === 'TIERED' ? 'Tiered' : 'Flat'}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  backgroundColor: tg.price.billingScheme === 'TIERED' ? '#dbeafe' : '#dcfce7',
                                  color: tg.price.billingScheme === 'TIERED' ? '#1e40af' : '#166534',
                                  fontWeight: 500,
                                }}
                              />
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="#64748b">
                              {tg.price?.displayInterval || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {tg.price?.usageType ? (() => {
                              const isMetered = tg.price.usageType.toUpperCase() === 'METERED';
                              return (
                                <Chip
                                  label={isMetered ? 'Metered' : 'Licensed'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    backgroundColor: isMetered ? '#fef3c7' : '#e0e7ff',
                                    color: isMetered ? '#92400e' : '#3730a3',
                                  }}
                                />
                              );
                            })() : '—'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} color="#1e293b">
                              {tg.price?.billingScheme !== 'TIERED' && tg.price?.unitAmount != null
                                ? formatCurrency(tg.price.unitAmount)
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tg.price?.tiers?.length || 0}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                minWidth: 28,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="#64748b">
                              {tg.createdAt ? new Date(tg.createdAt).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tg.deletedAt ? 'Deleted' : 'Active'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                backgroundColor: tg.deletedAt ? '#f1f5f9' : '#dcfce7',
                                color: tg.deletedAt ? '#64748b' : '#166534',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(tg);
                              }}
                              sx={{
                                color: '#94a3b8',
                                '&:hover': {
                                  color: '#ef4444',
                                  backgroundColor: '#fef2f2',
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

        {isAumRollout && (
          <PricesSection
            prices={prices}
            loading={pricesLoading}
            error={pricesError}
            parentGroupId={parentGroupId}
            onTestGroupAdded={() => refetch()}
          />
        )}
      </Box>

      <PriceDetailModal
        price={selectedPrice}
        open={!!selectedPrice}
        onClose={() => setSelectedPrice(null)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Remove from Test Group
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to remove this price from <strong>{deleteTarget?.groupId}</strong>?
          </Typography>
          {deleteTarget?.price && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: '#f8fafc',
                borderColor: '#e2e8f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                  {deleteTarget.price.id}
                </Typography>
                {deleteTarget.price.nickname && (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.75rem', ml: 1 }}>
                    - {deleteTarget.price.nickname}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteLoading}
            sx={{ color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' },
            }}
          >
            {deleteLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
